/**
 * Pots Ticker
 * The pots on a fixed two-line ticker tape (see Ticker), for phones: each
 * pot's name and size on top, who can win it underneath — or, once the hand
 * is over, who won each half. Same fixed height whatever's in it, so nothing
 * moves when side pots appear or the results come in. Carries the same
 * live-pot markers as LivePots for the gameplay e2e test (in a hidden list,
 * independent of what the tape is showing).
 */

import React from 'react';
import type { PotResult } from '../../store/types';
import { formatChips } from '../../utils/format';
import Ticker, { type TickerItem } from './Ticker';

interface PotsTickerProps {
  /** Mid-hand pots (main first) — from the server, empty with one pot only. */
  livePots: Array<{ amount: number; eligiblePlayers: string[] }>;
  /** The whole pot, for when there are no side pots. */
  pot: number;
  /** Once the hand has ended: each pot and who won it. */
  results?: PotResult[] | null;
  players: Array<{ id: string; username: string }>;
  youId?: string;
  /** This hand's ante and the next's, shown after the pots while the hand is live. */
  ante?: { current: number; next: number } | null;
}

const Cell: React.FC<{ top: React.ReactNode; bottom: React.ReactNode; dim?: boolean }> = ({ top, bottom, dim }) => (
  // A minimum width, so a pot's amount changing doesn't nudge the cells after it.
  <div className={`flex flex-col justify-center pl-2 pr-5 shrink-0 min-w-[150px] leading-tight ${dim ? 'opacity-60' : ''}`}>
    <div>{top}</div>
    <div className="text-gray-400">{bottom}</div>
  </div>
);

const AnteNow: React.FC<{ ante: { current: number } }> = ({ ante }) => (
  <>
    <span className="font-semibold text-white">Ante</span> <span className="text-yellow-400">{formatChips(ante.current)}</span>
  </>
);
const AnteNext: React.FC<{ ante: { current: number; next: number } }> = ({ ante }) => (
  <>{ante.next > ante.current ? `${formatChips(ante.next)} next hand` : 'stays the same'}</>
);

const PotsTicker: React.FC<PotsTickerProps> = ({ livePots, pot, results, players, youId, ante }) => {
  const username = new Map(players.map((p) => [p.id, p.username]));
  const name = (id: string, fallback?: string) => (id === youId ? 'You' : username.get(id) ?? fallback ?? '?');
  const potName = (i: number, count: number) => (count === 1 ? 'Pot' : i === 0 ? 'Main pot' : `Side pot ${i}`);

  let items: TickerItem[];
  if (results && results.length > 0) {
    // Hand over: who won each half of each pot.
    items = results.map((r, i) => {
      const winners = (icon: string, ws: PotResult['rankWinners']) =>
        ws.length > 0 && (
          <span className="mr-2">
            {icon}{' '}
            {ws.map((w, k) => (
              <span key={w.userId}>
                {k > 0 && ' & '}
                <span className={w.userId === youId ? 'text-yellow-300 font-semibold' : 'text-white font-semibold'}>
                  {name(w.userId, w.username)}
                </span>{' '}
                {w.handDescription} <span className="text-yellow-400">+{formatChips(w.amount)}</span>
              </span>
            ))}
          </span>
        );
      const node = (
        <Cell
          top={
            <>
              <span className="font-semibold text-white">{potName(i, results.length)}</span>{' '}
              <span className="text-yellow-400">{formatChips(r.amount)}</span>
            </>
          }
          bottom={
            <>
              {winners('🏆', r.rankWinners)}
              {winners('💎', r.jewelWinners)}
            </>
          }
        />
      );
      return { key: `result-${i}`, node };
    });
  } else {
    const pots = livePots.length > 1 ? livePots : [{ amount: pot, eligiblePlayers: [] as string[] }];
    const everyone = pots[0].eligiblePlayers.length;
    const whoCanWin = (ids: string[]) =>
      ids.length === 0 || ids.length >= everyone
        ? 'everyone'
        : ids.length <= 3
          ? ids.map((id) => name(id)).join(' & ')
          : `${ids.length} players`;
    items = pots.map((p, i) => {
      const node = (
        <Cell
          dim={!!youId && p.eligiblePlayers.length > 0 && !p.eligiblePlayers.includes(youId)}
          top={
            <>
              <span className="font-semibold text-white">{potName(i, pots.length)}</span>{' '}
              <span className="text-yellow-400">{formatChips(p.amount)}</span>
            </>
          }
          bottom={whoCanWin(p.eligiblePlayers)}
        />
      );
      return { key: `pot-${i}`, node };
    });
    if (ante) items.push({ key: 'ante', node: <Cell top={<AnteNow ante={ante} />} bottom={<AnteNext ante={ante} />} /> });
  }

  return (
    <>
    {/* For the gameplay e2e test, as LivePots / PotResults mark them: the
        mid-hand pots (only when there really are side pots), or once the hand
        is over each pot's result. A hidden list, since the tape repeats items. */}
    {!results?.length && livePots.length > 1 && (
      <div hidden>
        {livePots.map((p, i) => (
          <span key={i} data-testid="live-pot" data-amount={p.amount} />
        ))}
      </div>
    )}
    {!!results?.length && (
      <div hidden>
        {results.map((r, i) => (
          <span key={i} data-testid="pot-result" data-amount={r.amount} />
        ))}
      </div>
    )}
    <Ticker
      testId="pots-ticker"
      heightClass="h-[40px] shrink-0 text-[11px]"
      items={items}
      empty={<span className="px-2 text-gray-600 italic">No pot yet</span>}
    />
    </>
  );
};

export default PotsTicker;
